<template>
  <SharedEditDialog width="800">
    <template #title>
      <p class="title">{{ messages.editMyDetails }}</p>
    </template>
    <template #content>
      <Loading v-if="loading" class="loader"></Loading>
      <div v-else>
        <div class="d-flex flex-row flex-wrap pt-6">
          <div class="d-flex flex-column input-container mb-6">
            <label for="iban" class="mb-1 input-container__label">
              {{ messages.iban }}
              <v-tooltip bottom max-width="360px">
                <template #activator="{ on, attrs }">
                  <v-icon color="primary" dark v-bind="attrs" v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ $t("message.ibanHint") }}</span>
              </v-tooltip>
            </label>
            <input
              id="iban"
              v-model="bank.iban.value"
              type="text"
              :placeholder="messages.enterIban"
              name="iban"
              class="input-container__input"
              :class="{ 'input-container__input--error': bank.iban.errMsg }"
              @input="validateInputDefer($event, 'iban')"
              @blur="validateInputDefer($event, 'iban', 0)"
            />
            <p v-if="bank.iban.errMsg" class="input-container__error-message">
              {{ bank.iban.errMsg }}
            </p>
          </div>
          <div class="d-flex flex-column input-container mb-6">
            <label for="swift" class="mb-1 input-container__label"
              >{{ messages.swift }}

              <v-tooltip bottom max-width="360px">
                <template #activator="{ on, attrs }">
                  <v-icon color="primary" dark v-bind="attrs" v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                <span>{{ $t("message.swiftHint") }}</span>
              </v-tooltip>
            </label>
            <input
              id="swift"
              v-model="bank.swift.value"
              type="text"
              :placeholder="messages.enterSwift"
              name="swift"
              class="input-container__input"
              :class="{ 'input-container__input--error': bank.swift.errMsg }"
              @input="validateInputDefer($event, 'swift')"
              @blur="validateInputDefer($event, 'swift', 0)"
            />
            <p v-if="bank.swift.errMsg" class="input-container__error-message">
              {{ bank.swift.errMsg }}
            </p>
          </div>
          <div class="d-flex flex-column input-container mb-6">
            <label for="street" class="mb-1 input-container__label">{{ messages.street }}</label>
            <input
              id="street"
              v-model="address.street.value"
              type="text"
              :placeholder="messages.enterStreet"
              name="street"
              class="input-container__input"
              :class="{ 'input-container__input--error': address.street.errMsg }"
              @input="validateInputDefer($event, 'street')"
              @blur="validateInputDefer($event, 'street', 0)"
            />
            <p v-if="address.street.errMsg" class="input-container__error-message">
              {{ address.street.errMsg }}
            </p>
          </div>
          <div class="d-flex flex-column input-container mb-6">
            <label for="buildingNumber" class="mb-1 input-container__label">{{
              messages.buildingNumber
            }}</label>
            <input
              id="buildingNumber"
              v-model="address.buildingNumber.value"
              type="text"
              :placeholder="messages.enterBuildingNumber"
              name="buildingNumber"
              class="input-container__input"
              :class="{ 'input-container__input--error': address.buildingNumber.errMsg }"
              @input="validateInputDefer($event, 'buildingNumber')"
              @blur="validateInputDefer($event, 'buildingNumber', 0)"
            />
            <p v-if="address.buildingNumber.errMsg" class="input-container__error-message">
              {{ address.buildingNumber.errMsg }}
            </p>
          </div>
          <div class="d-flex flex-column input-container mb-6">
            <label for="apartmentNumber" class="mb-1 input-container__label">{{
              messages.apartmentNumber
            }}</label>
            <input
              id="apartmentNumber"
              v-model="address.apartmentNumber.value"
              type="text"
              :placeholder="messages.enterApartmentNumber"
              name="apartmentNumber"
              class="input-container__input"
              :class="{ 'input-container__input--error': address.apartmentNumber.errMsg }"
              @input="validateInputDefer($event, 'apartmentNumber')"
              @blur="validateInputDefer($event, 'apartmentNumber', 0)"
            />
            <p v-if="address.apartmentNumber.errMsg" class="input-container__error-message">
              {{ address.apartmentNumber.errMsg }}
            </p>
          </div>
          <div class="d-flex flex-column input-container mb-6">
            <label for="zip" class="mb-1 input-container__label">{{ messages.zip }}</label>
            <input
              id="zip"
              v-model="address.zip.value"
              type="text"
              :placeholder="messages.enterZip"
              name="zip"
              class="input-container__input"
              :class="{ 'input-container__input--error': address.zip.errMsg }"
              @input="validateInputDefer($event, 'zip')"
              @blur="validateInputDefer($event, 'zip', 0)"
            />
            <p v-if="address.zip.errMsg" class="input-container__error-message">
              {{ address.zip.errMsg }}
            </p>
          </div>
          <div class="d-flex flex-column input-container mb-6">
            <label for="city" class="mb-1 input-container__label">{{ messages.city }}</label>
            <input
              id="city"
              v-model="address.city.value"
              type="text"
              :placeholder="messages.enterCity"
              name="city"
              class="input-container__input"
              :class="{ 'input-container__input--error': address.city.errMsg }"
              @input="validateInputDefer($event, 'city')"
              @blur="validateInputDefer($event, 'city', 0)"
            />
            <p v-if="address.city.errMsg" class="input-container__error-message">
              {{ address.city.errMsg }}
            </p>
          </div>
          <div class="d-flex flex-column input-container mb-6">
            <label for="country" class="mb-1 input-container__label">{{ messages.country }}</label>
            <input
              id="country"
              v-model="address.country.value"
              type="text"
              :placeholder="messages.enterCountry"
              name="country"
              class="input-container__input"
              :class="{ 'input-container__input--error': address.country.errMsg }"
              @input="validateInputDefer($event, 'country')"
              @blur="validateInputDefer($event, 'country', 0)"
            />
            <p v-if="address.country.errMsg" class="input-container__error-message">
              {{ address.country.errMsg }}
            </p>
          </div>
        </div>
      </div>
    </template>
    <template #actions="{ closeDialog }">
      <BaseButton
        outlined
        :clear="true"
        color="grayscale-darken3"
        class="mr-4 cancel-button"
        @click="closeDialog"
        >{{ messages.void }}</BaseButton
      >

      <BaseButton
        :disabled="!isFormValid"
        class="confirm-button"
        @click="closeDialog(submitChangeProfileDetails)"
        >{{ messages.confirmChanges }}</BaseButton
      >
    </template>
  </SharedEditDialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import validateInputDefer from "@/mixins/validateInputDefer"
import spinnerMixin from "@/mixins/spinnerMixin"
import notyMixin from "@/mixins/notyMixin"
import parseDate from "@/mixins/parseDate"
import camelize from "camelize2"
import decamelize from "decamelize-keys-deep"
import { omit } from "@/utils/functions"

export default {
  mixins: [validateInputDefer, spinnerMixin, notyMixin, parseDate],

  data: () => ({
    modal: false,
    bank: {
      swift: {
        value: "",
        errMsg: "",
        timeout: null,
      },
      iban: {
        value: "",
        errMsg: "",
        timeout: null,
      },
    },
    address: {
      street: {
        value: "",
        errMsg: "",
        timeout: null,
      },
      buildingNumber: {
        value: "",
        errMsg: "",
        timeout: null,
      },
      apartmentNumber: {
        value: "",
        errMsg: "",
        timeout: null,
      },
      zip: {
        value: "",
        errMsg: "",
        timeout: null,
      },
      city: {
        value: "",
        errMsg: "",
        timeout: null,
      },
      country: {
        value: "",
        errMsg: "",
        timeout: null,
      },
    },
  }),
  computed: {
    messages() {
      return {
        editMyDetails: this.$t("message.editMyDetails"),
        swift: this.$t("message.profile.swift"),
        enterSwift: this.$t("message.profile.enterSwift"),
        iban: this.$t("message.profile.iban"),
        enterIban: this.$t("message.profile.enterIban"),
        street: this.$t("message.profile.street"),
        enterStreet: this.$t("message.profile.enterStreet"),
        buildingNumber: this.$t("message.profile.buildingNumber"),
        enterBuildingNumber: this.$t("message.profile.enterBuildingNumber"),
        apartmentNumber: this.$t("message.profile.apartmentNumber"),
        enterApartmentNumber: this.$t("message.profile.enterApartmentNumber"),
        zip: this.$t("message.profile.zip"),
        enterZip: this.$t("message.profile.enterZip"),
        city: this.$t("message.profile.city"),
        enterCity: this.$t("message.profile.enterCity"),
        country: this.$t("message.profile.country"),
        enterCountry: this.$t("message.profile.enterCountry"),
        cancel: this.$t("message.cancel"),
        confirm: this.$t("message.confirm"),
        void: this.$t("message.void"),
        confirmChanges: this.$t("message.confirmChanges"),
        approve: this.$t("message.approve"),
        close: this.$t("message.close"),
        error: {
          swift: this.$t("message.error.swift"),
          iban: this.$t("message.error.iban"),
          street: this.$t("message.error.street"),
          buildingNumber: this.$t("message.error.buildingNumber"),
          apartmentNumber: this.$t("message.error.apartmentNumber"),
          zip: this.$t("message.error.zip"),
          city: this.$t("message.error.city"),
          country: this.$t("message.error.country"),
        },
      }
    },
    isFormValid() {
      let anyErrors = false

      for (const element of Object.keys(this.bank)) {
        this.validateInput(this.bank[element].value, element)
        if (this.bank[element].errMsg) {
          anyErrors = true
        }
      }
      for (const element of Object.keys(this.address)) {
        this.validateInput(this.address[element].value, element)
        if (this.address[element].errMsg) {
          anyErrors = true
        }
      }
      return !anyErrors
    },
    ...mapGetters("layout", {
      dialogData: "getDialogConfig",
    }),
  },
  created() {
    const { swift, iban, address } = this.dialogData.data
    this.bank.swift.value = swift
    this.bank.iban.value = iban

    const addressData = camelize(omit(address || {}, "id"))
    Object.entries(addressData).forEach(([addressKey, addressValue]) => {
      this.address[addressKey].value = addressValue
    })
  },
  methods: {
    ...mapActions("profile", {
      updateProfile: "updateLanguages", //updates profile without removing falsy
    }),

    validateInput(value, type) {
      const rules = {
        iban: {
          regex: /^[A-Z]{2}\d{2}[A-Z0-9]{1,34}$/,
          required: true,
        },
        swift: {
          regex: /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
          required: this.bank.iban.value && this.bank.iban.value.substring(0, 2) !== "PL",
        },
        street: {
          regex:
            /^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9\s.'-]+(?:\s\d+[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż]?)?$/,
          required: true,
          max: 30,
        },
        buildingNumber: {
          regex: /^[\dA-Za-z]+$/,
          required: true,
          max: 10,
        },
        apartmentNumber: {
          regex: /^[\dA-Za-z]+$/,
          required: false,
          max: 10,
        },
        zip: {
          regex: /^[\d\s-]+$/,
          required: true,
          max: 30,
        },
        city: {
          regex: /^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż\s'-]+$/,
          required: true,
          max: 30,
        },
        country: {
          regex: /^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż\s'-]+$/,
          required: true,
          max: 30,
        },
      }
      const { required, regex, max } = rules[type]

      let isValid = !required
      if (value) {
        isValid = regex.test(value.replace(/\s/g, ""))
        if (max && isValid) isValid = value.length <= max
      }

      if (["iban", "swift"].includes(type))
        this.$set(this.bank[type], "errMsg", isValid ? "" : this.messages.error?.[type])
      else this.$set(this.address[type], "errMsg", isValid ? "" : this.messages.error?.[type])
    },

    async submitChangeProfileDetails() {
      if (this.isFormValid) {
        const address = decamelize(this.address)
        Object.keys(address).forEach(function (addressKey) {
          address[addressKey] = address[addressKey].value.trim()
        })
        const data = {
          swift: this.bank.swift.value.replace(/\s/g, ""),
          iban: this.bank.iban.value.replace(/\s/g, ""),
          address,
        }
        try {
          this.isLoading()
          await this.updateProfile(data)
          this.simpleSuccess({ text: this.$t("message.success.editProfile") })
        } catch (_) {
          this.simpleError({ text: this.$t("message.error.updateData") })
          return Promise.reject("failed to update profile data")
        } finally {
          this.isLoaded()
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/sass/rwd_breakpoints.scss";

.title {
  font-weight: 600;
  font-size: 1.25rem;
  color: var(--v-grayscale-darken3);
}

.cancel-button {
  width: 81px !important;
  height: 49px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: capitalize;
  border-radius: 8px;
  padding: 0;
  border: 1px solid var(--v-grayscale-lighten4);
  color: var(--v-grayscale-darken3) !important;
}
.flex-wrap {
  gap: 16px;
}

.input-container {
  position: relative;
  width: calc(50% - 8px);
  @media (max-width: $bp-mt) {
    width: 100%;
  }

  &__label {
    font-weight: 500;
    font-size: 1rem;
    color: var(--v-primary-darken3);
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 0.75em 0.5rem;
    background: var(--v-primary-lighten2);
    border: 1px solid var(--v-grayscale-lighten4);
    box-shadow: 0px 0px 0px rgba(0, 0, 0, 0.25);
    border-radius: 8px;

    &--error {
      color: var(--v-error-darken1);
      outline: 2px solid var(--v-error-darken1);
    }

    &:focus {
      outline: 3px solid var(--v-primary-base) !important;
    }
  }

  &__date-picker {
    background: var(--v-primary-lighten2);
    border: 1px solid var(--v-grayscale-lighten4);
    border-radius: 8px;
    height: 48px;
    width: 182px;
    padding: 0 1rem !important;
    display: flex;
    align-items: center;
    font-size: 1rem !important;

    &__button {
      padding: 0 1rem !important;
    }

    ::v-deep input {
      font-size: 1rem !important;
    }
  }
  &__error-message {
    position: absolute;
    bottom: -25px;
    font-size: 0.875rem;
    color: var(--v-error-darken1);
  }
}

.confirm-button {
  min-width: 167px;
}

.v-btn::before {
  display: none !important;
}

::v-deep .v-date-picker-title {
  display: flex;
  flex-direction: row-reverse;
}

::v-deep .v-picker__title__btn {
  color: white !important;
  font-size: 24px;
}

.loader {
  margin: 175px 0;
}
</style>
